import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faFilePdf,
  faFileCsv,
  faCalendarAlt,
  faDownload,
  faListAlt,
  faArchive,
  faHouseUser,
  faInfoCircle,
  faMoneyBillWave,
} from "@fortawesome/free-solid-svg-icons";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const RecordsPage = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loadingAllRecords, setLoadingAllRecords] = useState(false);
  const [loadingMonthlyRecords, setLoadingMonthlyRecords] = useState(false);
  const token = localStorage.getItem("token");
  const PropertyId = localStorage.getItem("selectedPropertyId");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [initialFetchLoading, setInitialFetchLoading] = useState(true);
  const [initialFetchError, setInitialFetchError] = useState(null);

  useEffect(() => {
    const fetchInitialRecords = async () => {
      setInitialFetchLoading(true);
      setInitialFetchError(null);
      if (!token || !PropertyId) {
        setInitialFetchError("Authentication token or property ID missing. Please log in and select a property.");
        setInitialFetchLoading(false);
        return;
      }
      try {
        const response = await axios.get(
          `${StandardURL}/iNyumba/viewrecordsrentlandlordyear/${PropertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setRecords(Array.isArray(response.data.response) ? response.data.response : []);
      } catch (error) {
        console.error("Error fetching initial records:", error);
        setInitialFetchError("Failed to fetch records overview. Please try again.");
        toast.error("Failed to fetch records overview.");
      } finally {
        setInitialFetchLoading(false);
      }
    };

    fetchInitialRecords();
  }, [token, PropertyId]);

  const handleDownload = async (format) => {
    setLoadingAllRecords(true);
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/reportsdownload/${PropertyId}?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: format === "pdf" ? "application/pdf" : "text/csv",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `inyumbapaidrentrecords.${format}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Successfully downloaded ${format.toUpperCase()} report!`);
    } catch (error) {
      console.error(`Error downloading ${format.toUpperCase()} file:`, error);
      toast.error(`Failed to download ${format.toUpperCase()} report.`);
    } finally {
      setLoadingAllRecords(false);
    }
  };

  const handleArchivedData = () => {
    navigate("/landlord/archiveddatarecords");
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const maskPhone = (phone) => {
    if (!phone) return "";
    return phone.slice(0, 3) + "***" + phone.slice(-4);
  };

  const handleDownloadByMonthAndYear = async (format) => {
    if (!selectedMonth || !selectedYear) {
      toast.error("Please select both month and year before downloading.");
      return;
    }

    setLoadingMonthlyRecords(true);
    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/reportsdownloadbymonth/${PropertyId}?format=${format}`,
        {
          month: selectedMonth,
          year: selectedYear,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );
      const blob = new Blob([response.data], {
        type: format === "pdf" ? "application/pdf" : "text/csv",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `inyumbapaidrentrecords_${selectedYear}_${selectedMonth}.${format}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Successfully downloaded monthly ${format.toUpperCase()} report!`);
    } catch (error) {
      console.error(`Error downloading ${format.toUpperCase()} file:`, error);
      toast.error(`Failed to download monthly ${format.toUpperCase()} report.`);
    } finally {
      setLoadingMonthlyRecords(false);
    }
  };

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
         
          <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Header and Back Button */}
            <div className="flex justify-between items-center mb-8 border-b pb-4">
              <button
                onClick={() => handleNavigation("/landlord/home")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 text-center flex-grow">
                <FontAwesomeIcon icon={faDownload} className="mr-3 text-blue-600" />
                Reports & Records
              </h1>
              <div className="w-auto sm:w-1/4"></div> {/* Spacer for alignment */}
            </div>

            {/* Section: Download All Paid Rent Records */}
            <div className="mb-8 p-6 bg-blue-50 rounded-lg shadow-md border border-blue-200">
              <h2 className="text-2xl font-bold text-blue-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faFilePdf} className="mr-3 text-blue-600" />
                Download All Paid Rent Records
              </h2>
              <p className="text-gray-700 mb-6">
                Generate a comprehensive report of all paid rent records for your selected property.
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleDownload("pdf")}
                  disabled={loadingAllRecords}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingAllRecords ? (
                    <Oval height={20} width={20} color="#FFF" secondaryColor="#FCB4B4" strokeWidth={5} strokeWidthSecondary={5} />
                  ) : (
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  )}
                  {loadingAllRecords ? "Generating PDF..." : "Download PDF"}
                </button>
                <button
                  onClick={() => handleDownload("csv")}
                  disabled={loadingAllRecords}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingAllRecords ? (
                    <Oval height={20} width={20} color="#FFF" secondaryColor="#D1FAE5" strokeWidth={5} strokeWidthSecondary={5} />
                  ) : (
                    <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  )}
                  {loadingAllRecords ? "Generating CSV..." : "Download CSV"}
                </button>
              </div>
            </div>

            {/* Section: Download Records by Month & Year */}
            <div className="mb-8 p-6 bg-emerald-50 rounded-lg shadow-md border border-emerald-200">
              <h2 className="text-2xl font-bold text-emerald-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faCalendarAlt} className="mr-3 text-emerald-600" />
                Download Records by Month & Year
              </h2>
              <p className="text-gray-700 mb-6">
                Select a specific month and year to download rent records for that period.
              </p>
              <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 mb-6">
                <div className="flex-1 w-full">
                  <label htmlFor="selectYear" className="block text-sm font-medium text-gray-700 mb-2">
                    Year
                  </label>
                  <input
                    id="selectYear"
                    type="number"
                    placeholder="YYYY"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="flex-1 w-full">
                  <label htmlFor="selectMonth" className="block text-sm font-medium text-gray-700 mb-2">
                    Month
                  </label>
                  <select
                    id="selectMonth"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500 sm:text-sm"
                  >
                    <option value="">-- Select Month --</option>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleDownloadByMonthAndYear("pdf")}
                  disabled={loadingMonthlyRecords}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMonthlyRecords ? (
                    <Oval height={20} width={20} color="#FFF" secondaryColor="#FCB4B4" strokeWidth={5} strokeWidthSecondary={5} />
                  ) : (
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  )}
                  {loadingMonthlyRecords ? "Generating PDF..." : "Download PDF"}
                </button>
                <button
                  onClick={() => handleDownloadByMonthAndYear("csv")}
                  disabled={loadingMonthlyRecords}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMonthlyRecords ? (
                    <Oval height={20} width={20} color="#FFF" secondaryColor="#D1FAE5" strokeWidth={5} strokeWidthSecondary={5} />
                  ) : (
                    <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  )}
                  {loadingMonthlyRecords ? "Generating CSV..." : "Download CSV"}
                </button>
              </div>
            </div>

            {/* Section: View Archived Data & Monthly Records */}
            <div className="p-6 bg-purple-50 rounded-lg shadow-md border border-purple-200">
              <h2 className="text-2xl font-bold text-purple-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faListAlt} className="mr-3 text-purple-600" />
                View Detailed Records
              </h2>
              <p className="text-gray-700 mb-6">
                Navigate to view archived records by year or detailed monthly records directly in the application.
              </p>
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={handleArchivedData}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faArchive} className="mr-2" />
                  View Archived Data (by Year)
                </button>
                <button
                  onClick={() => handleNavigation("/landlord/recordsview")}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
                  View Monthly Records (Detailed)
                </button>
              </div>
            </div>

            {/* Initial Records Display (if applicable and relevant to this component) */}
            {initialFetchLoading && (
              <div className="flex items-center justify-center py-10">
                <Oval
                  height={50}
                  width={50}
                  color="#6D28D9"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel='oval-loading'
                  secondaryColor="#C4B5FD"
                  strokeWidth={4}
                  strokeWidthSecondary={4}
                />
                <p className="ml-3 text-lg text-gray-700">Loading records overview...</p>
              </div>
            )}

            {initialFetchError && !initialFetchLoading && (
              <div className="text-center py-10 bg-red-50 rounded-lg border border-red-300 text-red-700">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-red-400 mb-4" />
                <p className="text-lg font-semibold mb-2">Error loading records overview!</p>
                <p className="text-md">{initialFetchError}</p>
              </div>
            )}

            {!initialFetchLoading && !initialFetchError && records.length > 0 && (
              <div className="overflow-x-auto mt-10 p-6 bg-white rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                  <FontAwesomeIcon icon={faListAlt} className="mr-2 text-gray-600" />
                  Recent Records Overview
                </h2>
                <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        House Number
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Water Bill
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Trash Bill
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Rent Bill
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Service Bill
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Total Amount Paid
                      </th>
                      <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {records.map((item, index) => (
                      <tr key={item.record_id || index} className="hover:bg-gray-50">
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800">
                          <FontAwesomeIcon icon={faHouseUser} className="mr-2 text-gray-500" />
                          {item.house_number || "N/A"}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(item.water_billbill || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(item.trash_bill || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(item.rent_bill || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(item.service_bill || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-800 font-bold text-green-700">
                          <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-green-600" />
                          KES {parseFloat(item.total_amount_paid || 0).toLocaleString()}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-gray-600 text-sm">
                          {item.created_at ? new Date(item.created_at).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!initialFetchLoading && !initialFetchError && records.length === 0 && (
              <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300 mt-10">
                <FontAwesomeIcon icon={faInfoCircle} className="text-5xl text-gray-400 mb-4" />
                <p className="text-lg text-gray-600">No records overview available.</p>
                <p className="text-md text-gray-500 mt-2">Use the options above to generate reports or view detailed data.</p>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default RecordsPage;